import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate, useParams } from "react-router-dom";

import { list } from "@material-tailwind/react";

import { FindPersonMode, findPersonModeOf, supportsCompanyOnlyMode } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import {
	useGetLeanListQuery,
	useGetListQuery,
	useUpdateListDetailsMutation,
} from "@/pages/Private/redux/list/list.api";

import { LoadingOverlay } from "@/components";

import { ButtonColor } from "@/components/Button/types";

import { ScrapeLevel } from "@/enum/job.enum";

import { EnrichmentType, ListSourceType, UpdateType } from "@/enum/list.enum";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { useUpdateApolloMutation } from "@/pages/Private/redux/apollo/apollo.api";

import { ApolloType } from "@/enum/apollo.enum";

import { CustomEventType } from "@/enum/custom-event.enum";

import { showGlobalToast } from "@/utils/showGlobalToast";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { StepInfo } from "@/components/StepInfo/StepInfo";

import { describeSaveFailure } from "@/utils/describeSaveFailure";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import { Stepper } from "./components/Stepper";
import { getAvailableSteps, isDetailsNeeded, getDoneSteps } from "../../utils/create-list-steps";
import { DefineIcps } from "./components/DefineIcps";
import { FindPersonModeSelector } from "./components/FindPersonModeSelector";
import { FindPersonContactModeSelector } from "./components/FindPersonContactModeSelector";
import { useFindPersonContactMode } from "./hooks/useFindPersonContactMode";
import { CountrySelection, IcpPosition } from "./enum/icp.enum";

import { FeaturesCodes } from "../../../Users/enum/codes";
import { OverrideRules } from "./components/OverrideRules";
import { PreviewSection } from "./components/PreviewSection";
import { TrackerFindPersonStep } from "./components/TrackerFindPersonStep";
import { OverrideRule } from "./types/override-rules";

export interface Icp {
	title?: string;
	countrySelection?: CountrySelection;
	/** @deprecated Use `countries` instead */
	country?: string;
	countries?: string[];
	regions?: string[];
	urn?: string;
	position?: IcpPosition;
	personaCategoryId?: number;
	decisionMakersOnly?: boolean;
	similarTitleSearch?: boolean;
	similarTitles?: {
		title: string;
		active: boolean;
	}[];
	department?: string[];
	seniority?: string[];
	excludedPersonaJobTitles?: string[];
	error?: boolean;
}
export interface EnrichmentFilterConfig {
	first?: Icp;
	second?: Icp;
	third?: Icp;
	overrideRules?: OverrideRule[];
}

export const ListEditDetails: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id = 0 } = useParams();

	const navigate = useNavigate();
	const ts = (key: string) => t(`list.${key}`);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const { data, isLoading } = useGetListQuery(+id);

	// PREC-4400: lead counts (companiesCount) live on the LEAN list payload only; the full GET above
	// carries none (apps/web/CLAUDE.md §17), so the "existing companies" note has to read the count
	// from here. Same cache key as the list detail page, so this is a warm hit, and the endpoint is
	// gated by PermissionCodes.LIST exactly like the full GET — no new 403 surface.
	const { data: leanList } = useGetLeanListQuery({ id: +id, assignStatus: undefined });

	const [updateSearch] = useUpdateApolloMutation();

	const [listType, setListType] = useState<ScrapeLevel | undefined>(data?.scrapeLevel || undefined);

	const [entityType, setEntityType] = useState<CsvEnrichmentType | CustomEventType | undefined>(
		data?.entityType || undefined
	);

	const [maxPerCompany, setMaxPerCompany] = useState<number | undefined>(
		data?.apollos?.[0]?.config?.enrichmentFilterConfig?.maxPerCompany || undefined
	);

	const [titleFilters, setTitleFilters] = useState<EnrichmentFilterConfig | undefined>(
		(data?.apollos?.[0]?.config?.enrichmentFilterConfig as EnrichmentFilterConfig) || undefined
	);

	const [overrideRules, setOverrideRules] = useState<OverrideRule[]>(
		(data?.apollos?.[0]?.config?.enrichmentFilterConfig as EnrichmentFilterConfig)?.overrideRules ||
			[]
	);

	// PREC-4400: "Find people now" vs "Only enrich companies, find people later". Stored in the
	// SOURCE apollo config, like maxPerCompany.
	const [findPersonMode, setFindPersonMode] = useState<FindPersonMode>(
		findPersonModeOf(data?.apollos?.[0]?.config)
	);

	// PREC-3899: "Use my contacts first" / "Find new people only", stored on the list itself.
	const contactMode = useFindPersonContactMode(data, listType, entityType);

	const [isDirty, setIsDirty] = useState(false);

	const [enrichmentType, setEnrichmentType] = useState<EnrichmentType | undefined>(
		EnrichmentType.AI_AGENT
	);

	useEffect(() => {
		if (data) {
			setListType(data.scrapeLevel || undefined);
			setEntityType(data.entityType || undefined);
			setEnrichmentType(data.enrichmentType || EnrichmentType.AI_AGENT);
			const filterConfig = data?.apollos?.[0]?.config
				?.enrichmentFilterConfig as EnrichmentFilterConfig;

			setTitleFilters(filterConfig);
			setMaxPerCompany(data?.apollos?.[0]?.config?.maxPerCompany || undefined);
			setOverrideRules(filterConfig?.overrideRules || []);
			setFindPersonMode(findPersonModeOf(data?.apollos?.[0]?.config));
		}
	}, [data]);

	const isDisabled = useMemo(() => {
		if (data) {
			if (
				[ListSourceType.JOBS, ListSourceType.MAPS, ListSourceType.PARALLEL_AI].includes(
					data?.sourceType
				)
			) {
				if (
					listType &&
					[ScrapeLevel.GET_INFO_AT, ScrapeLevel.GET_MANAGING_DIRECTOR].includes(listType)
				) {
					return !listType;
				}

				if (listType === ScrapeLevel.GET_ENRICHMENT) {
					return !listType || !enrichmentType || !titleFilters;
				}
			}

			if (
				[
					ListSourceType.CSV_ENRICHMENTS,
					ListSourceType.CUSTOM_EVENTS,
					ListSourceType.JOB_CHANGE,
				].includes(data?.sourceType)
			) {
				if (entityType === CsvEnrichmentType.PERSON) {
					return !entityType;
				}

				if (entityType === CsvEnrichmentType.ORGANIZATION) {
					return !entityType || !enrichmentType || !titleFilters;
				}

				if (entityType === CsvEnrichmentType.PERSON_BY_TITLE) {
					return !entityType || !enrichmentType;
				}
			}

			if (data?.sourceType === ListSourceType.FUNDING_ROUNDS) {
				return false;
			}

			return Object.values(titleFilters || {}).some((filter) => filter?.error);
		}

		return true;
	}, [data, listType, entityType, enrichmentType, titleFilters]);

	const hasErrors = useMemo(() => {
		return Object.values(titleFilters || {}).some((filter) => filter?.error);
	}, [titleFilters]);

	// PREC-4400: the mode choice is offered only for source types whose backend company scraper
	// actually honours it — the CSV company scraper (CSV_ENRICHMENTS / CUSTOM_EVENTS on an
	// ORGANIZATION list) and, since PREC-4586 part 1, Maps, Funding rounds and Parallel AI. Jobs is
	// part 2 and still charges regardless, so offering the mode there would promise "nothing
	// charged" and then charge. The predicate is shared with the backend so the UI's "may I offer
	// it?" cannot drift from the scraper's "do I honour it?".
	const showFindPersonMode = useMemo(
		() => !!data && supportsCompanyOnlyMode({ sourceType: data.sourceType, entityType }),
		[data, entityType]
	);

	// Only promise company-only behaviour (the Companies tab, the "nothing is charged" copy) where
	// the mode is actually offered; a stored COMPANY_ONLY on an unsupported list is never acted on.
	const companyOnly = showFindPersonMode && findPersonMode === FindPersonMode.COMPANY_ONLY;

	// PREC-4400: changing the mode on a list that already holds company rows applies to companies
	// added from now on; the ones waiting on the Companies tab stay there for a bulk Find person.
	const existingCompaniesNote = useMemo(() => {
		const waiting = Number(leanList?.companiesCount) || 0;

		if (waiting === 0) {
			return undefined;
		}

		return `This applies to companies added from now on. The ${waiting} ${
			waiting === 1 ? "company" : "companies"
		} already on the Companies tab stay there: select them on the Companies tab and use Enrich → Find person. The Companies tab stays until no company is left on it.`;
	}, [leanList?.companiesCount]);

	useEffect(() => {
		if (!hasErrors && isDirty) {
			setIsDirty(false);
		}
	}, [hasErrors, isDirty]);

	const [update, { isLoading: isUpdating }] = useUpdateListDetailsMutation();

	const saveSettings = async () => {
		if ((!listType && !entityType && data?.sourceType !== ListSourceType.FUNDING_ROUNDS) || !data) {
			return;
		}

		if (hasErrors) {
			setIsDirty(true);
			showGlobalToast(ts("detailsStep.incomplete"));

			return;
		}

		const apollo = data?.apollos?.find((apollo) => apollo.type === ApolloType.SOURCE);

		if (apollo && titleFilters) {
			await updateSearch({
				id: apollo.id,
				name: data?.name || "",
				type: ApolloType.SOURCE,
				config: {
					...apollo.config,
					enrichmentFilterConfig: {
						...titleFilters,
						overrideRules: overrideRules.length > 0 ? overrideRules : undefined,
					},
					maxPerCompany,
					findPersonMode,
				},
				listId: +id,
				updateType: "entityFilter",
			}).unwrap();
		} else if (apollo && !titleFilters) {
			const { enrichmentFilterConfig, ...rest } = apollo?.config || {};

			await updateSearch({
				id: apollo.id,
				name: data?.name || "",
				type: ApolloType.SOURCE,
				config: {
					...rest,
					// PREC-4586 (review M4): the mode is independent of the search profiles — a
					// Funding list without config still has to save "only enrich companies".
					findPersonMode,
				},
				listId: +id,
				updateType: "entityFilter",
			}).unwrap();
		}

		await update({
			id: data.id,
			scrapeLevel: listType,
			entityType: entityType as CsvEnrichmentType,
			enrichmentType,
			...contactMode.saveFields,
			updateType: UpdateType.SETTINGS,
		}).unwrap();
	};

	const submit = async (direction: "next" | "previous" = "next") => {
		if ((!listType && !entityType && data?.sourceType !== ListSourceType.FUNDING_ROUNDS) || !data) {
			return;
		}

		// A rejected save used to escape this handler as an unhandled rejection — no message, no
		// navigation, "Next does nothing" (PREC-4017: a 404 on the list's apollo). Say why, stay put.
		try {
			await saveSettings();
		} catch (error) {
			showGlobalToast(describeSaveFailure(error, ts("detailsStep.saveFailed")));

			return;
		}

		if (hasErrors) {
			return;
		}

		if (direction === "next") {
			if (isSequencerActive) {
				navigate(`/app/lists/create/copywriting/${data.id}`);
			} else {
				navigate(`/app/lists/create/settings/${data.id}`);
			}
		} else {
			navigate(`/app/lists/create/filter/${data.id}`);
		}
	};

	if (data?.sourceType === ListSourceType.TRACKER) {
		return <TrackerFindPersonStep list={data} />;
	}

	return isLoading || !data ? (
		<LoadingOverlay />
	) : (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-32" sourceTypeLabel={ts(data.sourceType)}>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(data), data, isSequencerActive)}
				currentStep="details"
				doneSteps={getDoneSteps(data)}
				listId={data.id}
			/>
			<StepInfo
				description={
					<p>
						Set up to 3 search profiles that run in priority order. We search Profile 1 first, then
						Profile 2, then Profile 3. If we still need more people, we broaden the search with AI.
					</p>
				}
				title="Define who to find at each qualifying company"
			/>
			<div className="w-full">
				{showFindPersonMode && (
					<FindPersonModeSelector
						existingCompaniesNote={existingCompaniesNote}
						onChange={setFindPersonMode}
						value={findPersonMode}
					/>
				)}
				{contactMode.visible && (
					<FindPersonContactModeSelector onChange={contactMode.setMode} value={contactMode.mode} />
				)}
				{data.sourceType === ListSourceType.MAPS && (
					<>
						{listType === ScrapeLevel.GET_ENRICHMENT && (
							<DefineIcps
								hasErrors={hasErrors && isDirty}
								list={data}
								maxPerCompany={maxPerCompany}
								setMaxPerCompany={setMaxPerCompany}
								setTitleFilters={setTitleFilters}
								titleFilters={titleFilters}
								companyOnly={companyOnly}
							/>
						)}
					</>
				)}

				{data.sourceType === ListSourceType.CUSTOM_EVENTS && (
					<>
						{entityType &&
							[CsvEnrichmentType.ORGANIZATION].includes(entityType as CsvEnrichmentType) && (
								<DefineIcps
									hasErrors={hasErrors && isDirty}
									list={data}
									maxPerCompany={maxPerCompany}
									setMaxPerCompany={setMaxPerCompany}
									setTitleFilters={setTitleFilters}
									titleFilters={titleFilters}
									companyOnly={companyOnly}
								/>
							)}
					</>
				)}

				{data.sourceType === ListSourceType.CSV_ENRICHMENTS && (
					<>
						{entityType &&
							[CsvEnrichmentType.ORGANIZATION].includes(entityType as CsvEnrichmentType) && (
								<DefineIcps
									hasErrors={hasErrors && isDirty}
									list={data}
									maxPerCompany={maxPerCompany}
									setMaxPerCompany={setMaxPerCompany}
									setTitleFilters={setTitleFilters}
									titleFilters={titleFilters}
									companyOnly={companyOnly}
								/>
							)}
					</>
				)}

				{data.sourceType === ListSourceType.JOBS && (
					<>
						{listType === ScrapeLevel.GET_ENRICHMENT && (
							<DefineIcps
								hasErrors={hasErrors && isDirty}
								list={data}
								maxPerCompany={maxPerCompany}
								setMaxPerCompany={setMaxPerCompany}
								setTitleFilters={setTitleFilters}
								titleFilters={titleFilters}
								companyOnly={companyOnly}
							/>
						)}
					</>
				)}

				{data.sourceType === ListSourceType.PARALLEL_AI && (
					<>
						{listType === ScrapeLevel.GET_ENRICHMENT && (
							<DefineIcps
								hasErrors={hasErrors && isDirty}
								list={data}
								maxPerCompany={maxPerCompany}
								setMaxPerCompany={setMaxPerCompany}
								setTitleFilters={setTitleFilters}
								titleFilters={titleFilters}
								companyOnly={companyOnly}
							/>
						)}
					</>
				)}

				{data.sourceType === ListSourceType.FUNDING_ROUNDS && (
					<>
						<DefineIcps
							hasErrors={hasErrors && isDirty}
							list={data}
							maxPerCompany={maxPerCompany}
							setMaxPerCompany={setMaxPerCompany}
							setTitleFilters={setTitleFilters}
							titleFilters={titleFilters}
							companyOnly={companyOnly}
						/>
					</>
				)}

				{/* Override Rules section - shown when ICP configuration is visible */}
				{titleFilters && (
					<OverrideRules
						infoText={t("defineIcps.overrideRulesInfoText")}
						onChange={setOverrideRules}
						rules={overrideRules}
					/>
				)}

				{/* Preview section - shown when ICP configuration is present */}
				{titleFilters && data?.id && (
					<PreviewSection contactLookups={data} listId={data.id} onBeforeAction={saveSettings} />
				)}
			</div>
			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center w-auto border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={listType === null}
						isLoading={isUpdating}
						title={t("basics.previous")}
						onClick={() => submit("previous")}
					/>
					<Button
						disabled={isDisabled}
						isLoading={isUpdating}
						title={t("basics.next")}
						onClick={() => submit("next")}
					/>
				</div>
			</div>
		</ListBuilderLayout>
	);
};
